import React, { useState } from 'react';

import FormTitle from 'components/PageLayout/FormTitle';
import UploadDocumentRequirements from 'components/common/UploadDocumentRequirements';
import UploadFile from 'components/common/UploadFile';
import { ButtonWithLoadingState } from 'components/curo-components/common/Button';
import Typography from 'components/curo-components/common/Typography';
import { CuroApplicationVariables } from 'enums/curo/Variables';
import { getApplicationSelector } from 'handlers/curo-handlers/selectors';
import { useAppSelector } from 'hooks/reduxHooks';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import { handleDocumentsUploaded, uploadDocuments } from 'thunks';
import styled from 'types/AppTheme';

import FailedDocumentsUploadModal from './FailedDocumentsUploadModal';
import SuccessDocumentsUploadedModal from './SuccessDocumentsUploadedModal';

const StyledList = styled.ul`
  list-style-type: disc;
  list-style-position: inside;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 12px;
`;

const StyledListItem = styled.li`
  display: flex;
  gap: 8px;
`;

const StyledListSpan = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 20px;
  height: 20px;
  border: 1px solid #3f4f68;
  border-radius: 6px;
`;

const StyledUploadFileWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 24px 0px 36px 0px;
`;

const MAX_FILE_SIZE = 0.5 * 1024 * 1024;
const ACCEPTED_FILE_TYPES = [
  '.zip',
  '.pdf',
  '.txt',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.csv',
  '.xml',
  '.json',
  '.ppt',
  '.pptx',
  '.png',
  '.jpg',
  '.jpeg',
  '.gif',
];

const AdditionalDocumentsSubmissionStep = () => {
  const application = useAppSelector(getApplicationSelector);
  const dispatchWithUnwrap = useDispatchWithUnwrap();

  const [modalType, setModalType] = useState<'success' | 'error' | null>(null);
  const [files, setFiles] = useState<File[]>([]);

  const handleFileChange = (file: File | null, index?: number) => {
    if (file) {
      setFiles([...files, file]);
      return;
    }

    if (index !== undefined) {
      setFiles(files.filter((_, i) => i !== index));
    }
  };

  const handleUploadDocuments = async () => {
    //CURO-TODO temporary solution
    let isDocumentsUploaded = false;
    try {
      if (application && files.length) {
        await dispatchWithUnwrap(
          uploadDocuments({
            applicationId: application.id,
            files,
          }),
        );

        isDocumentsUploaded = true;

        await dispatchWithUnwrap(handleDocumentsUploaded({ applicationId: application.id }));

        setModalType('success');
      }
    } catch (err) {
      // Handle documents upload error
      if (isDocumentsUploaded) {
        setModalType('error');
      }
    }
  };

  const closeModal = () => {
    if (modalType === 'success') {
      setFiles([]);
    }
    setModalType(null);
  };

  const renderModals = () => {
    switch (modalType) {
      case 'success':
        return <SuccessDocumentsUploadedModal onClose={closeModal} />;
      case 'error':
        return <FailedDocumentsUploadModal onClose={closeModal} />;
      default:
        return null;
    }
  };

  const getPageTitle = () => {
    return application && application.variables[CuroApplicationVariables.DocumentsUploaded]
      ? 'Document Upload - Continued'
      : 'Document Upload';
  };

  const getPageSubtitle = () => {
    return application && application.variables[CuroApplicationVariables.DocumentsUploaded] ? (
      <Typography>
        Your uploaded documents are currently under review.
        <br />
        <br />
        Please continue to upload any remaining documents:
      </Typography>
    ) : (
      <Typography>
        Please upload the following documents using our secure process. If the application process
        is not completed by{' '}
        {application?.variables[CuroApplicationVariables.ApplicationExpirationDate]}, your
        application will be considered withdrawn.
      </Typography>
    );
  };

  return (
    <>
      <FormTitle
        title={getPageTitle()}
        subTitle={
          <>
            {getPageSubtitle()}
            <StyledList>
              <StyledListItem>
                <StyledListSpan>1</StyledListSpan>
                Government Issued ID (For example: State issued driver's license or ID card,
                Passport)
              </StyledListItem>
              <StyledListItem>
                <StyledListSpan>2</StyledListSpan>
                Bank Statement
              </StyledListItem>
              <StyledListItem>
                <StyledListSpan>3</StyledListSpan>
                Most Recent Proof of Income
              </StyledListItem>
            </StyledList>
          </>
        }
      />
      <StyledUploadFileWrapper>
        <UploadFile
          onFileChange={handleFileChange}
          files={files}
          maxFileSize={MAX_FILE_SIZE}
          accept={ACCEPTED_FILE_TYPES}
        />
        <UploadDocumentRequirements />
      </StyledUploadFileWrapper>
      <ButtonWithLoadingState
        onClick={handleUploadDocuments}
        kind="primary"
        size="form"
        disabled={!files.length}
      >
        Upload Documents
      </ButtonWithLoadingState>
      {renderModals()}
    </>
  );
};

export default AdditionalDocumentsSubmissionStep;
